import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Upload, X, Paperclip, Lock } from "lucide-react";

export default function PublicOnboarding() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingQ, setUploadingQ] = useState(null);
  const fileInputs = useRef({});

  const load = () => api.get(`/public/onboarding/by-token/${token}`)
    .then(({ data }) => { setData(data); setAnswers(data.answers || {}); setFiles(data.files || {}); })
    .catch(() => setNotFound(true))
    .finally(() => setLoading(false));
  useEffect(() => { load(); }, [token]);

  const locked = data?.status === "complete";
  const setAns = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));

  const save = async (silent = false) => {
    setSaving(true);
    try {
      await api.put(`/public/onboarding/by-token/${token}`, { answers });
      if (!silent) toast.success("Progress saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const submit = async () => {
    const missing = (data.questions || []).filter((q) => q.required && q.type !== "file" && !(answers[q.id] || "").trim());
    if (missing.length) { toast.error(`Please answer: ${missing[0].label}`); return; }
    setSaving(true);
    try {
      const { data: updated } = await api.post(`/public/onboarding/by-token/${token}/submit`, { answers });
      setData(updated);
      toast.success("Thank you! Your onboarding has been submitted.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit");
    } finally { setSaving(false); }
  };

  const uploadFile = async (qid, file) => {
    if (!file) return;
    setUploadingQ(qid);
    const fd = new FormData();
    fd.append("question_id", qid);
    fd.append("file", file);
    try {
      const { data: res } = await api.post(`/public/onboarding/by-token/${token}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFiles((f) => ({ ...f, [qid]: [...(f[qid] || []), { file_id: res.file_id, filename: res.filename }] }));
      toast.success("File uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setUploadingQ(null); if (fileInputs.current[qid]) fileInputs.current[qid].value = ""; }
  };

  const removeFile = async (qid, fileId) => {
    try {
      await api.delete(`/public/onboarding/by-token/${token}/files/${fileId}`);
      setFiles((f) => ({ ...f, [qid]: (f[qid] || []).filter((x) => x.file_id !== fileId) }));
    } catch { toast.error("Could not remove file"); }
  };

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-24 font-mono text-sm text-zinc-500">Loading…</div>;
  if (notFound || !data) return (
    <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="onboarding-notfound">
      <h1 className="font-display font-black text-3xl tracking-tighter">Link not found</h1>
      <p className="mt-3 text-zinc-600">This onboarding link may be invalid. Please contact Brand Bloom.</p>
    </div>
  );

  const sections = [...new Set(data.questions.map((q) => q.section || "General"))];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="public-onboarding-page">
      <div className="mb-8">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Project Onboarding</span>
        <h1 className="mt-2 font-display font-black text-4xl tracking-tighter">Welcome, {data.client_name}</h1>
        <p className="mt-2 text-zinc-600">Tell us about your project so we can build exactly what you need. Your answers save automatically when you click Save — you can return anytime.</p>
      </div>

      {data.status === "submitted" && (
        <div className="mb-6 flex items-center gap-3 border border-green-300 bg-green-50 p-4" data-testid="onboarding-submitted-banner">
          <CheckCircle2 className="h-5 w-5 text-green-600" />
          <p className="text-sm text-green-800">Submitted — thank you! You can still update your answers below until our team finalizes onboarding.</p>
        </div>
      )}
      {locked && (
        <div className="mb-6 flex items-center gap-3 border border-zinc-300 bg-zinc-50 p-4" data-testid="onboarding-locked-banner">
          <Lock className="h-5 w-5 text-zinc-500" />
          <p className="text-sm text-zinc-700">This onboarding has been finalized and is now read-only.</p>
        </div>
      )}

      <div className="space-y-10">
        {sections.map((section) => (
          <section key={section}>
            <h2 className="font-display font-bold text-lg border-b-2 border-zinc-900 pb-2 mb-5">{section}</h2>
            <div className="space-y-6">
              {data.questions.filter((q) => (q.section || "General") === section).map((q) => (
                <div key={q.id} data-testid={`q-${q.id}`}>
                  <Label className="text-sm font-medium">{q.label}{q.required && <span className="text-primary ml-1">*</span>}</Label>
                  {q.help && <p className="text-xs text-zinc-400 mt-0.5">{q.help}</p>}
                  <div className="mt-2">
                    {q.type === "textarea" ? (
                      <Textarea data-testid={`input-${q.id}`} disabled={locked} value={answers[q.id] || ""} onChange={(e) => setAns(q.id, e.target.value)} className="rounded-none border-zinc-300 focus-visible:ring-primary min-h-[90px]" />
                    ) : q.type === "select" ? (
                      <Select disabled={locked} value={answers[q.id] || ""} onValueChange={(v) => setAns(q.id, v)}>
                        <SelectTrigger data-testid={`input-${q.id}`} className="rounded-none border-zinc-300"><SelectValue placeholder="Select…" /></SelectTrigger>
                        <SelectContent>
                          {(q.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : q.type === "file" ? (
                      <div>
                        <input ref={(el) => (fileInputs.current[q.id] = el)} type="file" hidden onChange={(e) => uploadFile(q.id, e.target.files?.[0])} />
                        <Button type="button" variant="outline" disabled={locked || uploadingQ === q.id} onClick={() => fileInputs.current[q.id]?.click()} data-testid={`upload-${q.id}`} className="rounded-none border-zinc-900 font-mono uppercase tracking-widest text-xs">
                          {uploadingQ === q.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1.5" /> Upload</>}
                        </Button>
                        <div className="mt-2 space-y-1.5">
                          {(files[q.id] || []).map((f) => (
                            <div key={f.file_id} className="flex items-center gap-2 text-sm bg-zinc-50 border border-zinc-200 px-3 py-1.5">
                              <Paperclip className="h-3.5 w-3.5 text-zinc-400" />
                              <span className="flex-1 truncate">{f.filename}</span>
                              {!locked && <button onClick={() => removeFile(q.id, f.file_id)} className="text-zinc-400 hover:text-red-600"><X className="h-4 w-4" /></button>}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Input data-testid={`input-${q.id}`} type={q.type === "url" ? "url" : q.type === "date" ? "date" : "text"} disabled={locked} value={answers[q.id] || ""} onChange={(e) => setAns(q.id, e.target.value)} className="rounded-none border-zinc-300 focus-visible:ring-primary" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      {!locked && (
        <div className="mt-10 flex flex-col sm:flex-row gap-3 sticky bottom-4">
          <Button onClick={() => save(false)} disabled={saving} variant="outline" data-testid="onboarding-save-btn" className="rounded-none border-zinc-900 font-mono uppercase tracking-widest h-12 flex-1">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Progress"}
          </Button>
          <Button onClick={submit} disabled={saving} data-testid="onboarding-submit-btn" className="rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest h-12 flex-1">
            {data.status === "submitted" ? "Update Submission" : "Submit Onboarding"}
          </Button>
        </div>
      )}
    </div>
  );
}
